import {
  BellIcon,
  BotIcon,
  CheckIcon,
  TrendingUpIcon,
} from "lucide-react";
import { Dispatch, SetStateAction } from "react";

import { NotificationCommsToSendList } from "./NotificationCommsToSendList";
import { NotificationExpiringCandidacyList } from "./NotificationExpiringCandidacyList";
import { NotificationInterviewsTodayList } from "./NotificationInterviewsTodayList";
import { NotificationLeaderboardDailyList } from "./NotificationLeaderboardDailyList";
import { NotificationLeaderboardWeeklyList } from "./NotificationLeaderboardWeeklyList";
import { NotificationMarkReadButton } from "./NotificationMarkReadButton";
import { NotificationMissingStartDatesList } from "./NotificationMissingStartDatesList";
import { NotificationPausedJobsList } from "./NotificationPausedJobsList";
import { NotificationPlacementCheckInsList } from "./NotificationPlacementCheckInsList";
import { NotificationStaleCandidaciesList } from "./NotificationStaleCandidaciesList";
import {
  Column,
  EmptyState,
  ErrorPlaceholder,
  LoadingIndicator,
  Row,
  Text,
} from "@/components";
import { PriorityCell } from "@/components/portfolio/table/cells/PriorityCell";
import { StafferPetIncentiveToken } from "@/components/staffer-pets/StafferPetIncentiveToken";
import { Button } from "@/components/ui/button";

import { useUpdateSetNotifications } from "@/hooks/updateSets/useUpdateSetNotifications";
import {
  notificationCategoryToMeta,
  notificationTypeToCategoryId,
} from "@/lib/constants";
import { dates } from "@/lib/dates";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import {
  MIXPANEL_NOTIFICATION_SURFACES,
  MIXPANEL_SCREENS,
} from "@/lib/mixpanel/types";
import { getNotificationLink } from "@/lib/notificationLinks";
import { topCandidates } from "@/lib/topCandidates";
import { cn } from "@/lib/utils";
import {
  NotificationActiveCandidateFinishedSteviePayload,
  NotificationCategoryId,
  NotificationCommsToSendPayload,
  NotificationIncentiveRedeemedPayload,
  NotificationExpiringCandidatesPayload,
  NotificationInterviewsTodayPayload,
  NotificationLeaderboardDailyPayload,
  NotificationLeaderboardWeeklyPayload,
  NotificationMissingStartDatesPayload,
  NotificationPausedJobsReminderPayload,
  NotificationPlacementCheckInsPayload,
  NotificationResponse,
  NotificationStaleCandidaciesPayload,
  NotificationType,
} from "@/types";

export const NotificationsTable = ({
  hasAnyNotifications,
  isCompact = false,
  isError,
  isPending,
  notifications,
  selectedIds,
  setSelectedIds,
  trackingSurface,
}: {
  hasAnyNotifications: boolean;
  isCompact?: boolean;
  isError: boolean;
  isPending: boolean;
  notifications: NotificationResponse[];
  selectedIds?: Set<number>;
  setSelectedIds?: Dispatch<SetStateAction<Set<number>>>;
  trackingSurface: MIXPANEL_NOTIFICATION_SURFACES;
}) => {
  const renderBody = () => {
    if (isPending) {
      return <LoadingIndicator />;
    }
    if (isError) {
      return <ErrorPlaceholder />;
    }
    if (notifications.length === 0) {
      if (!hasAnyNotifications) {
        return (
          <EmptyState
            className="py-16"
            description="All your notifications will appear here."
            Icon={BellIcon}
            title="No notifications yet"
          />
        );
      }
      return (
        <EmptyState
          className="py-16"
          description="Try a different search or filter."
          Icon={BellIcon}
          title="No matching notifications"
        />
      );
    }
    return (
      <Column className="divide-y divide-slate-100">
        {notifications.map((notification) => (
          <NotificationRow
            key={notification.id}
            isCompact={isCompact}
            notification={notification}
            selectedIds={selectedIds}
            setSelectedIds={setSelectedIds}
            trackingSurface={trackingSurface}
          />
        ))}
      </Column>
    );
  };

  const render = () => {
    return <Column>{renderBody()}</Column>;
  };

  return render();
};

const NotificationRow = ({
  isCompact,
  notification,
  selectedIds,
  setSelectedIds,
  trackingSurface,
}: {
  isCompact: boolean;
  notification: NotificationResponse;
  selectedIds?: Set<number>;
  setSelectedIds?: Dispatch<SetStateAction<Set<number>>>;
  trackingSurface: MIXPANEL_NOTIFICATION_SURFACES;
}) => {
  const { mutate: updateNotifications } = useUpdateSetNotifications();
  const categoryId = notificationTypeToCategoryId[notification.type];
  const isUnread = notification.readAt === null;
  const isSelected = selectedIds ? selectedIds.has(notification.id) : false;
  const notificationLink = getNotificationLink(
    notification.type,
    notification.payload,
  );

  const toggleSelected = () => {
    if (!setSelectedIds) return;
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(notification.id)) {
        next.delete(notification.id);
      } else {
        next.add(notification.id);
      }
      return next;
    });
  };

  const handleRowClick = () => {
    if (!notificationLink) return;
    mixpanelUtils.trackRowClick(
      "Notification",
      MIXPANEL_SCREENS.NOTIFICATIONS,
      {
        notification_type: notification.type,
        surface: trackingSurface,
      },
    );
    if (isUnread) {
      updateNotifications({
        notificationIds: [notification.id],
        markAsRead: true,
      });
    }
    window.open(notificationLink, "_blank");
  };

  const render = () => {
    // Not in the notification catalog, so this type is not supposed to be
    // rendered on the frontend or in Staffer at all
    if (!categoryId) return null;

    return (
      <Row
        className={cn(
          "group items-start gap-3 py-8",
          isCompact && "gap-2 py-5",
          notificationLink && "cursor-pointer",
        )}
        onClick={handleRowClick}
      >
        {isCompact ? null : (
          <NotificationIconCell
            categoryId={categoryId}
            isSelected={isSelected}
            isUnread={isUnread}
            onToggleSelected={toggleSelected}
          />
        )}
        <NotificationContentCell
          isCompact={isCompact}
          isUnread={isUnread}
          notification={notification}
          trackingSurface={trackingSurface}
        />
        <NotificationActionsCell
          isCompact={isCompact}
          isUnread={isUnread}
          notification={notification}
          trackingSurface={trackingSurface}
        />
      </Row>
    );
  };

  return render();
};

const NotificationIconCell = ({
  categoryId,
  isSelected,
  isUnread,
  onToggleSelected,
}: {
  categoryId: NotificationCategoryId;
  isSelected: boolean;
  isUnread: boolean;
  onToggleSelected: () => void;
}) => {
  const { Icon, className } = notificationCategoryToMeta[categoryId];

  const render = () => (
    <Button
      className={cn(
        "mt-0.5 h-9 w-9 shrink-0 rounded-full p-0",
        isSelected
          ? "bg-primary-600 text-white hover:bg-primary-700"
          : className,
        !isSelected && !isUnread && "opacity-60",
      )}
      onClick={(e) => {
        e.stopPropagation();
        onToggleSelected();
      }}
      size="icon"
      variant="ghost"
    >
      {isSelected ? (
        <CheckIcon className="h-4 w-4" strokeWidth={2.5} />
      ) : (
        <Icon className="h-4 w-4" />
      )}
    </Button>
  );

  return render();
};

const NotificationContentCell = ({
  isCompact,
  isUnread,
  notification,
  trackingSurface,
}: {
  isCompact: boolean;
  isUnread: boolean;
  notification: NotificationResponse;
  trackingSurface: MIXPANEL_NOTIFICATION_SURFACES;
}) => {
  const isStevieCall =
    notification.type === NotificationType.ACTIVE_CANDIDATE_FINISHED_STEVIE;
  const stevie = isStevieCall
    ? (notification.payload as NotificationActiveCandidateFinishedSteviePayload)
    : null;
  const isElevated =
    stevie?.fromScore != null && stevie.toScore > stevie.fromScore;
  // The FE owns this type's copy — the stored title/content are a fallback.
  const incentive =
    notification.type === NotificationType.INCENTIVE_REDEEMED
      ? (notification.payload as NotificationIncentiveRedeemedPayload)
      : null;
  const title = incentive ? "Incentive redeemed!" : notification.title;
  const content = incentive
    ? `${incentive.redeemerFirstName} cashed in an incentive token for ${incentive.description}!`
    : notification.content;

  const render = () => (
    <Column className="min-w-0 flex-1 gap-1">
      <Row className="items-center gap-2">
        {isUnread ? (
          <span
            className={cn(
              "h-2 w-2 shrink-0 rounded-full bg-primary-600",
              isCompact && "h-1.5 w-1.5",
            )}
          />
        ) : null}
        <Text
          className={cn(
            isCompact ? "text-xs" : "text-sm",
            isUnread
              ? "font-semibold text-slate-900"
              : "font-normal text-slate-500",
          )}
        >
          {title}
          {/* Title ends on "Stevie call", so the bot rides right after it. */}
          {isStevieCall ? (
            <BotIcon
              className={cn(
                "ml-1 inline align-text-bottom text-primary-700",
                isCompact ? "h-4 w-4" : "h-5 w-5",
              )}
            />
          ) : null}
          {/* Rides with the bot so the rating jump reads off the title. */}
          {isElevated ? (
            <TrendingUpIcon
              className={cn(
                "ml-1 inline align-text-bottom text-emerald-600",
                isCompact ? "h-4 w-4" : "h-5 w-5",
              )}
            />
          ) : null}
        </Text>
        {/* Sibling of the title Text so the token square never gets inlined
            into a truncating paragraph. */}
        {incentive ? <StafferPetIncentiveToken sizePx={16} /> : null}
      </Row>
      <Text
        className={cn(
          "line-clamp-2 font-normal",
          isCompact ? "text-xs" : "text-sm",
          isUnread ? "text-slate-500" : "text-slate-400",
        )}
      >
        {content}
      </Text>
      {notification.type === NotificationType.EXPIRING_CANDIDATES ? (
        <NotificationExpiringCandidacyList
          isCompact={isCompact}
          payload={
            notification.payload as NotificationExpiringCandidatesPayload
          }
          trackingSurface={trackingSurface}
        />
      ) : null}
      {notification.type === NotificationType.INTERVIEWS_TODAY ? (
        <NotificationInterviewsTodayList
          isCompact={isCompact}
          payload={notification.payload as NotificationInterviewsTodayPayload}
          trackingSurface={trackingSurface}
        />
      ) : null}
      {notification.type === NotificationType.PLACEMENT_CHECKINS ? (
        <NotificationPlacementCheckInsList
          isCompact={isCompact}
          payload={notification.payload as NotificationPlacementCheckInsPayload}
          trackingSurface={trackingSurface}
        />
      ) : null}
      {notification.type === NotificationType.MISSING_START_DATES ? (
        <NotificationMissingStartDatesList
          isCompact={isCompact}
          payload={notification.payload as NotificationMissingStartDatesPayload}
          trackingSurface={trackingSurface}
        />
      ) : null}
      {notification.type === NotificationType.LEADERBOARD_DAILY ? (
        <NotificationLeaderboardDailyList
          isCompact={isCompact}
          payload={notification.payload as NotificationLeaderboardDailyPayload}
          trackingSurface={trackingSurface}
        />
      ) : null}
      {notification.type === NotificationType.LEADERBOARD_WEEKLY ? (
        <NotificationLeaderboardWeeklyList
          isCompact={isCompact}
          payload={notification.payload as NotificationLeaderboardWeeklyPayload}
          trackingSurface={trackingSurface}
        />
      ) : null}
      {notification.type === NotificationType.PAUSED_JOBS_REMINDER ? (
        <NotificationPausedJobsList
          isCompact={isCompact}
          payload={
            notification.payload as NotificationPausedJobsReminderPayload
          }
          trackingSurface={trackingSurface}
        />
      ) : null}
      {notification.type === NotificationType.COMMS_TO_SEND ? (
        <NotificationCommsToSendList
          payload={notification.payload as NotificationCommsToSendPayload}
          trackingSurface={trackingSurface}
        />
      ) : null}
      {notification.type === NotificationType.STALE_CANDIDACIES ? (
        <NotificationStaleCandidaciesList
          payload={notification.payload as NotificationStaleCandidaciesPayload}
          trackingSurface={trackingSurface}
        />
      ) : null}
    </Column>
  );

  return render();
};

const NotificationActionsCell = ({
  isCompact,
  isUnread,
  notification,
  trackingSurface,
}: {
  isCompact: boolean;
  isUnread: boolean;
  notification: NotificationResponse;
  trackingSurface: MIXPANEL_NOTIFICATION_SURFACES;
}) => {
  const priority = topCandidates.priority(notification);

  const render = () => (
    <Column className="shrink-0 items-end justify-between gap-1 self-stretch">
      <Row
        className="items-center gap-1"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <Row className="invisible items-center gap-1 group-hover:visible">
          <NotificationMarkReadButton
            isCompact={isCompact}
            isUnread={isUnread}
            notificationId={notification.id}
            notificationType={notification.type}
            trackingSurface={trackingSurface}
          />
        </Row>
      </Row>
      {priority ? (
        <Row className="shrink-0 [&_button]:border-transparent">
          <PriorityCell isSmall job={{ priority }} readOnly />
        </Row>
      ) : null}
      <Text
        className={cn("text-xs text-slate-400", isCompact && "text-[11px]")}
      >
        {dates.timeAgoForPipeline(notification.createdAt)}
      </Text>
    </Column>
  );

  return render();
};
